import L from "leaflet";
import { LAYER_GROUPS } from "./geozoneLayers";
import { buildEd269PopupHtml } from "./ed269Popup";
import { belgianAltitudeMeters } from "../../../shared/belgianGeozones";
import { partitionByReachability } from "../../../shared/geozoneAltitudeFilter";
import { ed269WebValidEndTs, filterExpiredEd269Features } from "./ed269Expiry";
import { smoothRing } from "@client/lib/ringSimplify";
import { defaultGeozoneSelection } from "../../../shared/geozoneCategories";
import { ed269SourceInfo } from "../../../shared/ed269SourceInfo";

// Re-export der leaflet-freien Ablauf-Logik (eigene Quelle in ed269Expiry.ts,
// damit der tsx-Test-Runner sie ohne leaflet importieren kann).
export { ed269WebValidEndTs, filterExpiredEd269Features };

// Glättet runde Konturen einer ED-269-Geometrie für die Darstellung. GeoJSON-
// Koordinaten liegen als [lng, lat] vor (lonIndex 0). Echte Ecken/Donut-Löcher
// bleiben erhalten; nur grobe Kreis-Polygone werden verdichtet. Verfälscht die
// Datengrundlage nicht – die Glättung dient nur der Karte.
function smoothEd269Geometry<T extends GeoJSON.Geometry>(geom: T): T {
  if (!geom) return geom;
  if (geom.type === "Polygon") {
    return {
      ...geom,
      coordinates: (geom.coordinates as number[][][]).map(
        (ring) => smoothRing(ring as [number, number][], 0),
      ),
    } as T;
  }
  if (geom.type === "MultiPolygon") {
    return {
      ...geom,
      coordinates: (geom.coordinates as number[][][][]).map((poly) =>
        poly.map((ring) => smoothRing(ring as [number, number][], 0)),
      ),
    } as T;
  }
  return geom;
}

// Geteilte ED-269-Vektor-Geozonen-Helfer (AT/BE/DK) sowie die Default-Layer-
// Berechnung. Wird sowohl von der vollständigen Karte (MapPage) als auch von der
// eingebetteten Planungs-Karte (PlanningGeozoneMap) genutzt, damit beide
// Oberflächen NICHT auseinanderdriften (eine Quelle für Farben/Beschriftungen).

// Default-AN-Layer, die nicht über die Kategorie-Defaults laufen
// (Tieffluggebiete). ED-269-Vektorzonen + kombinierte Länder-Schalter kommen
// jetzt aus defaultGeozoneSelection() (shared/geozoneCategories).
export const EXTRA_GEOZONE_DEFAULTS: Record<string, boolean> = {
  de_lfa: true,
  at_geozones: true,
  be_geozones: true,
  dk_geozones: true,
};

// Alle Standard-AN-Geozonen-Layer (ohne localStorage-Override). Für die
// Planungs-Karte, die immer mit den Defaults rendert. Jetzt kategoriebasiert:
// alle Kategorien + Länder-Gesamt-Schalter + LFA sind standardmäßig AN.
export function defaultGeozoneLayers(): Record<string, boolean> {
  return defaultGeozoneSelection();
}

// Umschließendes Rechteck einer ED-269-FeatureCollection. Liefert null, wenn
// keine Features.
export function vectorFeaturesBounds(features?: GeoJSON.Feature[]): L.LatLngBounds | null {
  if (!features || features.length === 0) return null;
  try {
    const b = L.geoJSON({ type: "FeatureCollection", features } as GeoJSON.FeatureCollection).getBounds();
    return b.isValid() ? b : null;
  } catch {
    return null;
  }
}

// Normalisiert die ED-269-Beschränkungs-/Typ-Schreibweisen verschiedener
// Anbieter (Belgien/Droneguide nutzt `type` mit REQ_AUTH/RESTRICTED, Österreich
// `restriction` mit REQ_AUTHORISATION). Spiegelt die native Logik.
export function ed269WebRestriction(p: Record<string, any>): string {
  // Belgien (skeyes) liefert restriction als Objekt { type }; AT/DK als String.
  const restrictionRaw =
    p.restriction && typeof p.restriction === "object" ? p.restriction.type : p.restriction;
  const raw = (restrictionRaw ?? p.type ?? p.zoneRestriction ?? "").toString();
  const v = raw.toUpperCase().trim().replace(/[\s-]+/g, "_");
  switch (v) {
    case "REQ_AUTH":
    case "REQ_AUTHORISATION":
    case "REQ_AUTHORIZATION":
    case "AUTHORISATION":
    case "AUTHORIZATION":
    case "REQUIRES_AUTHORISATION":
      return "REQ_AUTHORISATION";
    case "PROHIBITED":
    case "FORBIDDEN":
    case "NO_FLY":
      return "PROHIBITED";
    case "RESTRICTED":
      return "RESTRICTED";
    case "CONDITIONAL":
    case "CONDITIONS":
      return "CONDITIONAL";
    case "NO_RESTRICTION":
    case "NONE":
    case "INFORMATION":
    case "INFO":
      return "NO_RESTRICTION";
    default:
      return v;
  }
}

// ED-269-Restriction -> Leaflet-Stilfarbe. Spiegelt die native Logik
// (ed269RestrictionColors) für die Web-Karte.
export function ed269WebColors(restriction: string): { color: string; fill: string } {
  switch ((restriction || "").toUpperCase()) {
    case "PROHIBITED":
      return { color: "#ff3b30", fill: "#ff3b30" };
    case "RESTRICTED":
      return { color: "#ff453a", fill: "#ff6347" };
    case "REQ_AUTHORISATION":
      return { color: "#ff9500", fill: "#ff9500" };
    case "CONDITIONAL":
      return { color: "#ff9f0a", fill: "#ffcc00" };
    case "NO_RESTRICTION":
      return { color: "#34c759", fill: "#34c759" };
    // Naturschutz (Naturområder): kräftiges Waldgrün, bewusst dunkler als das
    // systemGrün von NO_RESTRICTION (spiegelt shared ed269RestrictionColors).
    case "NATURE":
      return { color: "#1b5e20", fill: "#2e7d32" };
    default:
      return { color: "#007aff", fill: "#007aff" };
  }
}

export const ED269_WEB_LABELS: Record<string, string> = {
  PROHIBITED: "Verboten",
  RESTRICTED: "Beschränkt",
  REQ_AUTHORISATION: "Genehmigung erforderlich",
  CONDITIONAL: "Bedingt zulässig",
  NO_RESTRICTION: "Keine Beschränkung",
  NATURE: "Naturschutz",
};

// Wandelt einen ED-269-Höhenwert in Meter um (SFC/GND -> 0, UNL -> unbegrenzt,
// FT -> Meter). Spiegelt die native ed269LimitMeters-Logik.
export function ed269WebLimitMeters(value: any, uom: string): number | undefined {
  if (value == null) return undefined;
  const s = value.toString().toUpperCase().trim();
  if (s === "SFC" || s === "GND" || s === "0") return 0;
  if (s === "UNL" || s === "UNLIMITED" || s === "GND_UNL") return undefined;
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  const u = (uom || "").toString().toUpperCase();
  if (u === "FT" || u === "F" || u === "FEET") return n * 0.3048;
  return n;
}

// Untergrenze einer ED-269/skeyes-Zone in Metern (für Höhen-Filter + Anzeige).
export function ed269WebLowerMeters(p: Record<string, any>): number | undefined {
  const uom = (p.uomDimensions ?? p.uom ?? p.unit ?? "M").toString();
  return typeof p.lowerMeters === "number"
    ? p.lowerMeters
    : p.lowerAltitude && typeof p.lowerAltitude === "object"
      ? belgianAltitudeMeters(p.lowerAltitude)
      : ed269WebLimitMeters(p.lowerLimit ?? p.lower, uom);
}

// Höhenbezug der Untergrenze (roher Quellwert, von der Shared-Logik normalisiert).
export function ed269WebLowerReference(p: Record<string, any>): string | undefined {
  return (p.lowerAltitude && typeof p.lowerAltitude === "object"
    ? p.lowerAltitude.reference
    : undefined) ??
    p.lowerReference ??
    p.lowerVerticalReference ??
    p.lowerLimitReference;
}

export function ed269WebAltitude(p: Record<string, any>): string {
  const uom = (p.uomDimensions ?? p.uom ?? p.unit ?? "M").toString();
  // Belgien (skeyes): lowerAltitude/upperAltitude als Objekt { value, unit }.
  const lower = ed269WebLowerMeters(p);
  const upper =
    typeof p.upperMeters === "number"
      ? p.upperMeters
      : p.upperAltitude && typeof p.upperAltitude === "object"
        ? belgianAltitudeMeters(p.upperAltitude)
        : ed269WebLimitMeters(p.upperLimit ?? p.upper, uom);
  const fmtLower = lower == null ? "GND" : `${Math.round(lower)} m`;
  const fmtUpper = upper == null ? "unbegrenzt" : `${Math.round(upper)} m`;
  return `${fmtLower} – ${fmtUpper}`;
}

// localStorage-Schlüssel für den Sicherheits-Schalter "Alle Geozonen anzeigen".
// Von MapPage UND der Planungs-Karte geteilt, damit beide identisch reagieren.
export const GEOZONE_SHOW_ALL_KEY = "copter.map.geozoneShowAll.v1";

export function loadGeozoneShowAll(): boolean {
  try {
    return localStorage.getItem(GEOZONE_SHOW_ALL_KEY) === "1";
  } catch {
    return false;
  }
}

export function saveGeozoneShowAll(value: boolean): void {
  try {
    localStorage.setItem(GEOZONE_SHOW_ALL_KEY, value ? "1" : "0");
  } catch {}
}

// Höhen-Filter: ED-269-Features (AT/BE/DK) mit Untergrenze über der erreichbaren
// Höhe ausblenden. WMS-Länder sind hier ohnehin nicht betroffen.
export function filterEd269Features(
  features: GeoJSON.Feature[],
  reachableAltitudeM: number,
  terrainElevationM?: number | null,
): { visible: GeoJSON.Feature[]; hiddenAbove: GeoJSON.Feature[] } {
  const zones = (features || []).map((f) => {
    const p = (f.properties || {}) as Record<string, any>;
    return { lowerMeters: ed269WebLowerMeters(p), lowerReference: ed269WebLowerReference(p), feature: f };
  });
  const part = partitionByReachability(zones, { reachableAltitudeM, terrainElevationM });
  return {
    visible: part.visible.map((z) => z.feature),
    hiddenAbove: part.hiddenAbove.map((z) => z.feature),
  };
}

// Rendert eine ED-269-Geozonen-FeatureCollection (AT/BE/DK) in einen Leaflet-
// Layer: restriktionsabhängige Farbe + Popup (Name, Quelle, Beschränkung, Höhe).
export function renderEd269Layer(
  target: L.LayerGroup,
  features: GeoJSON.Feature[],
  sourceLabel: string,
  // Optional: aktiviert in jedem Popup einen "Nur diese Zone anzeigen"-Button,
  // der diese Zone isoliert öffnet (Planungs-Karte). Ohne Callback bleibt das
  // Verhalten der großen Karte (MapPage) unverändert (kein Button).
  onFocusZone?: (feature: GeoJSON.Feature) => void,
): void {
  const smoothed = features.map((f) =>
    f && f.geometry ? { ...f, geometry: smoothEd269Geometry(f.geometry) } : f,
  );
  L.geoJSON({ type: "FeatureCollection", features: smoothed } as GeoJSON.FeatureCollection, {
    pointToLayer: (_feature, latlng) => L.circleMarker(latlng, {
      radius: 5, color: "rgba(0,122,255,0.85)", weight: 2,
      fillColor: "rgba(0,122,255,0.85)", fillOpacity: 0.4,
    }),
    style: (feat) => {
      const p = (feat?.properties || {}) as Record<string, any>;
      const c = ed269WebColors(ed269WebRestriction(p));
      return { color: c.color, weight: 1.5, fillColor: c.fill, fillOpacity: 0.18, smoothFactor: 0 };
    },
    onEachFeature: (feat, lyr) => {
      const p = (feat.properties || {}) as Record<string, any>;
      const restriction = ed269WebRestriction(p);
      const restrictionLabel = p.informationalOnly ? "Infrastrukturhinweis; örtliche Regeln maßgeblich" : ED269_WEB_LABELS[restriction] || restriction || "Beschränkung";
      const name = (p.name || p.identifier || p.zoneId || "Geozone").toString();
      const altitude = ed269WebAltitude(p);
      const sourceInfo = ed269SourceInfo(p);

      // Antrags-/Genehmigungs-Hinweise mit anklickbaren Links (dänische Zonen:
      // Trafikstyrelsen-Verfahren via virk.dk bzw. Verteidigungskommando bei
      // Militärzonen).
      const html = buildEd269PopupHtml({
        name,
        sourceLabel,
        restrictionLabel,
        altitude,
        detail: [(p.message || p.reason || "").toString(), ...sourceInfo.details].filter(Boolean).join("\n"),
        appText: (p.applicationText || "").toString(),
        links: [
          { label: (p.applicationUrlLabel || "Antrag stellen").toString(), url: (p.applicationUrl || "").toString() },
          { label: (p.infoUrlLabel || "Weitere Infos").toString(), url: (p.infoUrl || "").toString() },
          { label: (p.contactEmail || "").toString(), url: (p.contactEmail || "").toString() },
          { label: (p.notamsUrlLabel || "NOTAM-/Luftraumkarte öffnen").toString(), url: (p.notamsUrl || "").toString() },
          ...sourceInfo.links,
        ],
        focusButtonLabel: onFocusZone ? "Nur diese Zone anzeigen" : "",
      });
      lyr.bindPopup(html);
      // Button-Klick im Popup an den Callback binden. popupopen feuert bei jedem
      // Öffnen erneut; { once: true } verhindert Mehrfach-Handler.
      if (onFocusZone) {
        lyr.on("popupopen", (e: L.PopupEvent) => {
          const node = e.popup.getElement();
          const btn = node?.querySelector("[data-ed269-focus]") as HTMLElement | null;
          if (!btn) return;
          btn.addEventListener(
            "click",
            () => {
              lyr.closePopup();
              onFocusZone(feat as GeoJSON.Feature);
            },
            { once: true },
          );
        });
      }
    },
  }).addTo(target);
}

// Holt serverseitig gecachte ED-269-Vektor-Geozonen für ein Land (at/be/dk).
// Der Endpoint liefert {countries:[{country, features:[]}]} statt einer
// FeatureCollection, daher die manuelle Extraktion.
export async function fetchVectorGeozones(country: "at" | "be" | "dk"): Promise<GeoJSON.Feature[]> {
  const r = await fetch(`/api/cached/geozones?country=${country}`, { credentials: "include" });
  if (!r.ok) return [];
  const data = await r.json();
  const entry = Array.isArray(data?.countries)
    ? data.countries.find((c: any) => (c?.country || "").toLowerCase() === country) ?? data.countries[0]
    : null;
  const features = entry?.features;
  return Array.isArray(features) ? (features as GeoJSON.Feature[]) : [];
}
